<template>
  <ModalDialog
    ref="dialog"
    title="Scan Task Items Despatched"
    submitTitle="Flag Despatched"
    :submitting="submittingDespatch"
    :submitDisabled="submitDisabled"
    icon="fa-truck"
    closeTitle="Cancel"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <div>
      <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
        <transition name="fade">
          <NotificationBar v-if="scanResult.message" :type="scanResult.type">
            <NotificationBarTitle>{{scanResult.message}}</NotificationBarTitle>
          </NotificationBar>
        </transition>
        <FormRow label="Barcode Scan">
          <FormInputText ref="scanBarcodeEntry" v-model="scanning.barcode" field="barcode" @enter="onBarcodeEntered" note="Use a barcode scanner or manually enter the task item barcode & press enter" />
        </FormRow>
        <FormRow label="Force Complete Production" v-if="isForceProductionAvailable" helpTip="If set, will force orders in production status to be completed">
          <FormCheckbox v-model="settings.forceCompleteProduction" />
        </FormRow>
        <FormRow label="Force Skip COD" v-if="isForceProductionAvailable && settings.forceCompleteProduction" helpTip="If set, will skip pending COD after completing production if applicable">
          <FormCheckbox v-model="settings.forceSkipCOD" />
        </FormRow>
        <FormRow label="Status">
          <FormStaticLabel>
            <div :class="{'result-fg-success': availableCount > 0}">
              {{availableCount}} task item{{availableCount | displayCountPlural}} ready for despatch
            </div>
            <div v-if="errorCount > 0" class="result-fg-failed">
              <strong>Warning:</strong> {{errorCount}} task item{{errorCount | displayCountPlural}} have errors and cannot be despatched
            </div>
          </FormStaticLabel>
        </FormRow>
        <div class="mt-2"> </div>
        <ContentTitle>Items Scanned</ContentTitle>
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th>Task</th>
              <th>Task Type</th>
              <th>Item Barcode</th>
              <th>Item</th>
              <th>Item Status</th>
              <th>Linked Order Info</th>
              <th>Despatch Info</th>
              <th>Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <template v-for="taskItem in taskItemList">
              <tr :key="taskItem.id">
                <td class="font-weight-bold">
                  <LinkTask :id="taskItem.task.id" :name="taskItem.task.sequenceNumber" />
                </td>
                <td>
                  <LabelBadge :labelColour="taskItem.task.taskType.highlightColour">{{taskItem.task.taskType.name}}</LabelBadge>
                </td>
                <td>{{taskItem.barcode}}</td>
                <td>{{taskItem.name}}</td>
                <td>
                  <LabelBadge :labelClass="`label-taskItem-status-${taskItem.status}`">{{taskItem.status | formatLookupName(TaskItemStatusTypeLookup)}}</LabelBadge>
                </td>
                <td>{{taskItem.linkDisplay}}</td>
                <td>
                  <span v-if="taskItem.hasError" class="font-weight-bold result-fg-failed">{{taskItem.error}}</span>
                  <span v-if="!taskItem.hasError" class="result-fg-success">Available for Despatch</span>
                </td>
                <td>
                  <ActionDropMenu :tableAction="true">
                    <DropdownMenuItem icon="fa-trash-alt" @click="removeTaskItem(taskItem)">Remove Item</DropdownMenuItem>
                  </ActionDropMenu>
                </td>
              </tr>
            </template>
          </template>
          <template v-slot:empty v-if="taskItemList.length == 0">
            Task Item list is empty
          </template>
        </StaticDataTable>
      </FormGroup>
    </div>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import StaticDataTable from '@/components/table/staticDataTable.vue'

import { TaskStatusTypes, TaskItemStatusTypeLookup, TaskItemTypes, TaskItemStatusTypes, AccountSettingKeys } from '@/store/types'
import FormCheckbox from '@/components/form/formCheckbox.vue'

export default {
  name: 'ScanTaskItemDespatchedDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    StaticDataTable,
    FormCheckbox
  },
  data() {
    return {
      dialogForm: {},
      scanning: {
        barcode: '',
      },
      settings: {
        forceCompleteProduction: false,
        forceSkipCOD: false
      },
      scanResult: {
        message: '',
        type: ''
      },
      loadingTask: false,
      submittingDespatch: false,
      taskItemList: []
    }
  },
  created() {
    this.TaskItemStatusTypeLookup = TaskItemStatusTypeLookup
  },
  mounted() {
    this.focusBarcode()
  },
  watch: {
    isForceProductionAvailable: {
      immediate: true,
      handler: function() {
        // Default the setting to on
        if(this.isForceProductionAvailable && !this.settings.forceCompleteProduction) {
          this.settings.forceCompleteProduction = true
          this.settings.forceSkipCOD = true
        }
      }
    }
  },
  computed: {
    ...mapState({
      accountSettings: state => state.account.accountSettings
    }),
    errorCount() {
      return this.taskItemList.filter(taskItem => taskItem.hasError).length
    },
    availableCount() {
      return this.taskItemList.filter(taskItem => !taskItem.hasError).length
    },
    submitDisabled() {
      if(this.taskItemList.length == 0)
        return true

      if(this.errorCount > 0)
        return true
      return false
    },
    isForceProductionAvailable() {
      return parseInt(this.accountSettings[AccountSettingKeys.taskDespatchScanForceProductionEnabled] || 0) != 0
    }
  },
  methods: {
    show() {
      this.dialogForm = { }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      let scanDialog = this
      if(this.availableCount > 0 && this.errorCount == 0) {

        this.confirmMessage('Confirm Task Items Despatched', '', { html: `This wil flag <strong>${this.availableCount}</strong> task item(s) as despatched and will generate <strong>invoices</strong> for linked orders if applicable`})
        .then(success => {
          if(success) {

            // Submit the despatch request
            this._submitTaskItemListDespatched()
          } else {

            // Focus after a timeout to let msgbox close with animations
            setTimeout(() => {
              scanDialog.focusBarcode()
            }, 500)
          }
        })
      }
    },
    async _submitTaskItemListDespatched() {
      this.submittingDespatch = true

      this.dialogForm = this.clearFormResponse()
      this.clearResultMessage()

      var taskItemIdList = this.taskItemList.map(taskItem => taskItem.id)
      
      var batchSize = 1     // Do one at a time to avoid issues with large transactions on server
      var taskItemChunkList = _.chunk(taskItemIdList, batchSize)

      // Break into submitting into chunks to process better with UX
      var submitSuccess = true
      for(var taskItemIdGroup of taskItemChunkList) {
        try {
          await this.$apiService.tasksService.taskItemForceDespatchList({
            taskItemIdList: taskItemIdGroup,
            forceCompleteProduction: this.settings.forceCompleteProduction,
            forceSkipCOD: this.settings.forceSkipCOD
          })

          // Remove task items from the list
          this.taskItemList = this.taskItemList.filter(taskItem => taskItemIdGroup.indexOf(taskItem.id) != -1)

        } catch(err) {
          this.dialogForm = this.processFormResponse(err)
          this.focusBarcode()
          submitSuccess = false
          break
        }
      }

      this.submittingDespatch = false
      if(submitSuccess) {
        this.modalClosed(true)
        this.hide()
      }
    },
    onBarcodeEntered(ev) {
      if(ev)
        ev.preventDefault()
      
      this.dialogForm = this.clearFormResponse()

      // Reset
      this.clearResultMessage()
      this.loadingTask = true

      this.$apiService.tasksService.runTaskBarcodeLookup(this.scanning.barcode)
      .then(response => {
        if(!response.data.taskItem) {
          this.setResultMessage(`Barcode is not associated with a task item`, 'error')
          return
        }

        let taskItem = response.data.taskItem

        var errorMessage = ''

        if(taskItem.type != TaskItemTypes.orderDespatch)
          errorMessage = 'Task Item is not an order despatch task item type'
        
        if(taskItem.status == TaskItemStatusTypes.completed)
          errorMessage = 'Task Item has already been completed'
        else if(taskItem.status == TaskItemStatusTypes.cancelled)
          errorMessage = 'Task Item has been cancelled'

        if(taskItem.task.status == TaskStatusTypes.completed)
          errorMessage = 'Task has already been completed'
        else if(taskItem.task.status == TaskStatusTypes.cancelled)
          errorMessage = 'Task has been cancelled'

        var listTaskItemInfo = {
          id: taskItem.id,
          type: taskItem.type,
          barcode: taskItem.barcode,
          status: taskItem.status,
          name: taskItem.name,
          description: taskItem.description,
          task: taskItem.task,
          linkedItemCount: (taskItem.taskItemLinkedItems || []).length,
          hasError: errorMessage != '',
          error: errorMessage
        }

        listTaskItemInfo.linkDisplay = `${listTaskItemInfo.linkedItemCount} item${this.displayCountPlural(listTaskItemInfo.linkedItemCount)}`

        // Ignore if already in the list
        var existingTaskItemIndex = this.taskItemList.findIndex(curTaskItem => curTaskItem.id == taskItem.id)
        if(existingTaskItemIndex != -1) {
          this.setResultMessage(`Task Item ${taskItem.barcode} already in the list! Task Item updated`, 'warning')

          // Update the status details
          // In case the task item was fixed & a re-scan will cause it to be reupdated
          this.taskItemList[existingTaskItemIndex] = listTaskItemInfo
          return
        }

        this.taskItemList.push(listTaskItemInfo)

        if(errorMessage != '')
          this.setResultMessage(`Task Item ${taskItem.barcode} added to the list with errors`, 'error')
        else
          this.setResultMessage(`Task Item ${taskItem.barcode} added to the list`, 'success')
      })
      .catch(err => {
        this.setResultMessage(this.processFormResponse(err).errorMessage, 'error')
      })
      .finally(() => {
        this.scanning.barcode = ''

        this.focusBarcode()
      })
    },

    clearResultMessage() {
      this.scanResult.message = ''
      this.scanResult.type = ''
    },

    setResultMessage(message, type) {
      this.scanResult.message = message
      this.scanResult.type = type
    },

    removeTaskItem(taskItem) {
      this.taskItemList = this.taskItemList.filter(listTaskItem => listTaskItem.id != taskItem.id)
      this.setResultMessage(`Task Item ${taskItem.barcode} removed from the list`, 'warning')
      this.focusBarcode()
    },
    focusBarcode() {
      this.$nextTick(() => {
        this.$refs.scanBarcodeEntry.focus()
        this.$refs.scanBarcodeEntry.select()
      })
    }
  }
}
</script>